import { deleteToken, getToken } from "firebase/messaging";
import { getFirebaseMessaging } from "../lib/firebase";
import postService from "../Services/post-service";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
export const PUSH_NOTIFICATIONS_ENABLED_KEY = "pushNotificationsEnabled";
const FCM_TOKEN_KEY = "fcmToken";
const FCM_TOKEN_IS_ADMIN_KEY = "fcmTokenIsAdmin";

const pushNotificationResult = (enabled, message = "") => ({
  enabled,
  message,
});

const pushNotificationFailure = (message, error) => {
  if (error) {
    console.error("Push notification setup failed:", message, error);
  } else {
    console.warn("Push notification setup failed:", message);
  }

  return pushNotificationResult(false, message);
};

const getFirebaseServiceWorkerRegistration = async () => {
  if (!("serviceWorker" in navigator)) return undefined;

  const config = new URLSearchParams({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
    appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
  });

  await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${config.toString()}`);

  return navigator.serviceWorker.ready;
};

export const setupPushNotifications = async (user, options = {}) => {
  const {
    forceRegister = false,
    requestPermission = true,
  } = options;

  try {
    if (!user) return pushNotificationFailure("No logged-in user was found.");
    if (!VAPID_KEY) return pushNotificationFailure("Firebase VAPID key is missing.");
    if (typeof Notification === "undefined") {
      return pushNotificationFailure("This browser does not support notifications.");
    }
    if (!window.isSecureContext) {
      return pushNotificationFailure("Push notifications require HTTPS or localhost.");
    }

    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      return pushNotificationFailure("Firebase messaging is not supported in this browser.");
    }

    const permission = requestPermission
      ? await Notification.requestPermission()
      : Notification.permission;

    if (permission !== "granted") {
      return pushNotificationFailure("Notification permission was not granted.");
    }

    const serviceWorkerRegistration = await getFirebaseServiceWorkerRegistration();

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration,
    });

    if (!token) {
      return pushNotificationFailure("Firebase did not return a push notification token.");
    }

    const adminToken = isAdminUser(user);
    const existingToken = localStorage.getItem(FCM_TOKEN_KEY);
    const existingTokenIsAdmin = localStorage.getItem(FCM_TOKEN_IS_ADMIN_KEY) === "true";

    if (existingToken === token && existingTokenIsAdmin === adminToken && !forceRegister) {
      localStorage.setItem(PUSH_NOTIFICATIONS_ENABLED_KEY, "true");
      return pushNotificationResult(true);
    }

    const deviceId = getOrCreateDeviceId();

    await postService.registerNotificationToken({
      token,
      deviceType: "WEB",
      deviceId,
      isAdmin: adminToken,
    });

    localStorage.setItem(FCM_TOKEN_KEY, token);
    localStorage.setItem(FCM_TOKEN_IS_ADMIN_KEY, String(adminToken));
    localStorage.setItem(PUSH_NOTIFICATIONS_ENABLED_KEY, "true");
    return pushNotificationResult(true);
  } catch (err) {
    return pushNotificationFailure(err?.message || "Push notification setup failed.", err);
  }
};

export const disablePushNotifications = async () => {
  localStorage.setItem(PUSH_NOTIFICATIONS_ENABLED_KEY, "false");

  try {
    const messaging = await getFirebaseMessaging();
    if (messaging) {
      await deleteToken(messaging);
    }
  } catch (err) {
    console.error("Push notification disable failed:", err);
  } finally {
    localStorage.removeItem(FCM_TOKEN_KEY);
    localStorage.removeItem(FCM_TOKEN_IS_ADMIN_KEY);
  }
};

export const isPushNotificationsEnabled = () => {
  return (
    localStorage.getItem(PUSH_NOTIFICATIONS_ENABLED_KEY) === "true" &&
    typeof Notification !== "undefined" &&
    Notification.permission === "granted"
  );
};

const getOrCreateDeviceId = () => {
  let id = localStorage.getItem("deviceId");

  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("deviceId", id);
  }

  return id;
};

const isAdminUser = (user) => {
  const role = String(user?.userRole || localStorage.getItem("userRole") || "").toUpperCase();
  const adminRoles = [
    "ADMIN",
    "SUPER_ADMIN",
    "OPERATION_MANAGER",
    "PROJECT_MANAGER",
    "FINANCE_MANAGER",
    "OPERATOR",
  ];

  return adminRoles.some((adminRole) => role === adminRole || role.includes(adminRole));
};
